import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Hls from 'hls.js';
import './LiveStream.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

const LiveStream = () => {
  const { streamId } = useParams();
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewerStreams, setViewerStreams] = useState([]);
  const [submittingStream, setSubmittingStream] = useState(false);
  const [viewerName, setViewerName] = useState('');
  const [viewerLocation, setViewerLocation] = useState('');
  const videoRef = useRef(null);
  const localStreamRef = useRef(null);
  const hlsRef = useRef(null);

  const loadStream = React.useCallback(async () => {
    try {
      setError(null);
      console.log(`[LiveStream] Loading stream: ${streamId}`);
      
      // Add timeout to prevent indefinite loading
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(`${API_BASE_URL}/streams/${streamId}`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      console.log(`[LiveStream] Response status: ${response.status}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[LiveStream] Stream data received:', data);
        
        const streamData = data.data || data;
        setStream(streamData);
        
        // Load video player if stream is active
        if (streamData && streamData.isActive && streamData.hlsUrl && videoRef.current) {
          console.log('[LiveStream] Initializing video player with HLS URL:', streamData.hlsUrl);
          
          // Clean up previous HLS instance
          if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
          }
          
          if (Hls.isSupported()) {
            const hls = new Hls({
              enableWorker: true,
              lowLatencyMode: true,
              backBufferLength: 90
            });
            
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
              console.log('[LiveStream] HLS manifest parsed, starting playback');
              if (videoRef.current) {
                videoRef.current.play().catch(err => {
                  console.error('[LiveStream] Error playing video:', err);
                });
              }
            });
            
            hls.on(Hls.Events.ERROR, (event, data) => {
              console.error('[LiveStream] HLS error:', data);
              if (data.fatal) {
                switch (data.type) {
                  case Hls.ErrorTypes.NETWORK_ERROR:
                    console.error('[LiveStream] Fatal network error, trying to recover');
                    hls.startLoad();
                    break;
                  case Hls.ErrorTypes.MEDIA_ERROR:
                    console.error('[LiveStream] Fatal media error, trying to recover');
                    hls.recoverMediaError();
                    break;
                  default:
                    console.error('[LiveStream] Fatal error, destroying HLS');
                    hls.destroy();
                    setError('Failed to load video stream. Please refresh the page.');
                    break;
                }
              }
            });
            
            hls.loadSource(streamData.hlsUrl);
            hls.attachMedia(videoRef.current);
            hlsRef.current = hls;
          } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
            console.log('[LiveStream] Using native HLS support');
            videoRef.current.src = streamData.hlsUrl;
            videoRef.current.play().catch(err => {
              console.error('[LiveStream] Error playing video:', err);
            });
          } else {
            console.warn('[LiveStream] HLS not supported in this browser');
            setError('Video streaming is not supported in your browser.');
          }
        } else {
          console.log('[LiveStream] Stream is not active or no HLS URL');
        }
      } else {
        const errorText = await response.text();
        console.error(`[LiveStream] API error (${response.status}):`, errorText);
        setError(`Failed to load stream: ${response.status === 404 ? 'Stream not found' : 'Server error'}`);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('[LiveStream] Request timeout');
        setError('Request timed out. The server may be unavailable.');
      } else {
        console.error('[LiveStream] Error loading stream:', error);
        setError(`Failed to load stream: ${error.message || 'Network error'}`);
      }
    } finally {
      setLoading(false);
    }
  }, [streamId]);

  const loadViewerStreams = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/streams/${streamId}/viewer-streams`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setViewerStreams(data.data || []);
      }
    } catch (error) {
      console.error('Error loading viewer streams:', error);
    }
  }, [streamId]);

  useEffect(() => {
    if (streamId) {
      loadStream();
      loadViewerStreams();
      
      // Poll for updates (only if stream exists and is not in error state)
      const interval = setInterval(() => {
        if (!error) {
          loadStream();
          loadViewerStreams();
        }
      }, 30000); // Poll every 30 seconds instead of 5 to reduce load
      
      return () => {
        clearInterval(interval);
        // Clean up HLS instance on unmount
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
      };
    } else {
      setError('No stream ID provided');
      setLoading(false);
    }
  }, [streamId, loadStream, loadViewerStreams, error]);

  const submitViewerStream = async () => {
    if (!viewerName || submittingStream) return;

    try {
      setSubmittingStream(true);
      
      // Get user media (camera)
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      
      localStreamRef.current = stream;
      
      // Create WebRTC offer (simplified - would need proper signaling)
      // For now, just submit the request
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/streams/${streamId}/viewer-streams`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          viewerName,
          viewerLocation,
          quality: 'auto',
        }),
      });
      
      if (response.ok) {
        alert('Your stream has been submitted! You\'re in the queue to be projected.');
        setViewerName('');
        setViewerLocation('');
        await loadViewerStreams();
      }
    } catch (error) {
      console.error('Error submitting viewer stream:', error);
      alert('Failed to submit stream. Make sure you allow camera/microphone access.');
    } finally {
      setSubmittingStream(false);
    }
  };

  if (loading) {
    return (
      <div className="live-stream-page">
        <div className="live-stream-loading">
          <div className="live-stream-loading-spinner"></div>
          <p>Loading stream...</p>
          <p className="live-stream-loading-hint">This may take a few moments</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="live-stream-page">
        <div className="live-stream-error">
          <h2>Stream Error</h2>
          <p>{error}</p>
          <button onClick={() => {
            setError(null);
            setLoading(true);
            loadStream();
          }} className="btn btn-orange">
            Retry
          </button>
          <a href="/events" className="btn btn-secondary" style={{ marginLeft: '1rem' }}>
            Back to Events
          </a>
        </div>
      </div>
    );
  }

  if (!stream) {
    return (
      <div className="live-stream-page">
        <div className="live-stream-error">
          <h2>Stream Not Found</h2>
          <p>The stream you're looking for does not exist or is no longer available.</p>
          <a href="/events" className="btn btn-orange">
            Back to Events
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="live-stream-page">
      <div className="live-stream-container">
        <div className="live-stream-header">
          <h1>{stream.title}</h1>
          {stream.isActive && (
            <span className="live-badge">🔴 LIVE</span>
          )}
        </div>

        {/* Main Stream Player */}
        <div className="live-stream-player">
          {stream.isActive && stream.hlsUrl ? (
            <video
              ref={videoRef}
              controls
              autoPlay
              playsInline
              className="stream-video"
            />
          ) : (
            <div className="stream-offline">
              <p>Stream is offline</p>
              {stream.scheduledStart && (
                <p>Starts: {new Date(stream.scheduledStart).toLocaleString()}</p>
              )}
            </div>
          )}
        </div>

        {/* Viewer Stream Submission */}
        {stream.isActive && stream.allowViewerStreams && (
          <div className="viewer-stream-section">
            <h2>🎥 Join the Dance Floor!</h2>
            <p>Submit your stream to be projected on the venue's screen</p>
            
            <div className="viewer-stream-form">
              <input
                type="text"
                placeholder="Your name"
                value={viewerName}
                onChange={(e) => setViewerName(e.target.value)}
                className="viewer-input"
              />
              <input
                type="text"
                placeholder="Your location (optional)"
                value={viewerLocation}
                onChange={(e) => setViewerLocation(e.target.value)}
                className="viewer-input"
              />
              <button
                onClick={submitViewerStream}
                disabled={!viewerName || submittingStream}
                className="submit-stream-btn"
              >
                {submittingStream ? 'Submitting...' : 'Submit My Stream'}
              </button>
            </div>

            {viewerStreams.length > 0 && (
              <div className="viewer-queue">
                <h3>Viewer Queue ({viewerStreams.length})</h3>
                <div className="viewer-list">
                  {viewerStreams.map((viewer) => (
                    <div
                      key={viewer.id}
                      className={`viewer-item ${viewer.isProjected ? 'projected' : ''}`}
                    >
                      <span className="viewer-name">{viewer.viewerName}</span>
                      {viewer.viewerLocation && (
                        <span className="viewer-location">📍 {viewer.viewerLocation}</span>
                      )}
                      {viewer.isProjected && (
                        <span className="projected-badge">✨ On Projector</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stream Info */}
        <div className="stream-info">
          {stream.description && <p>{stream.description}</p>}
          {stream.platforms && stream.platforms.length > 0 && (
            <div className="platform-links">
              <p>Also streaming on:</p>
              {stream.platforms
                .filter(p => p.isStreaming && p.platformUrl)
                .map((platform) => (
                  <a
                    key={platform.id}
                    href={platform.platformUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="platform-link"
                  >
                    {platform.platform}
                  </a>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveStream;

